import React, { useState, useEffect,useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  Input,
  Button,
  Card,
  message,
  Spin,
} from "antd";
import { PlusOutlined, LoadingOutlined, EyeOutlined } from "@ant-design/icons";
import CustomerEntitySelect from "../components/CustomerEntitySelect";
import axios from "../axiosSetup";
import { formatMoney } from "../utils/formatMoney";
import Breadcrumb from "../components/Breadcrumbs";
import { useOverflowTooltip } from "../hooks/useOverflowTooltip";
import CustomTooltip from "../components/CustomTooltip";
import "./MarginCalculator.css";

const sortParamMap = {
  totalPrice: "orderByTotalPrice",
  grossMargin: "orderByGrossMargin",
  marginPercent: "orderByMarginPercent",
};

const formatCreatedDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getCreatedByName = (createdBy) => {
  if (!createdBy) return "-";
  if (typeof createdBy === "string") return createdBy;

  return (
    createdBy.name ||
    createdBy.fullName ||
    createdBy.email ||
    createdBy.username ||
    "-"
  );
};

const MarginCalculator = () => {
  const navigate = useNavigate();

  const [quotes, setQuotes] = useState([]);
  const [customerList, setCustomerList] = useState([]);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [hasLoadedQuotesOnce, setHasLoadedQuotesOnce] = useState(false);
  const [isCustomerLoading, setIsCustomerLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [quoteFilter, setQuoteFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
const [totalPages, setTotalPages] = useState(1);
const [quoteInput, setQuoteInput] = useState(""); 
const [sortField, setSortField] = useState("");
const [sortOrder, setSortOrder] = useState("");
const lastFetchKeyRef = React.useRef(null);
const customersFetchedRef = React.useRef(false);
const STATE_KEY = "MARGIN_CALCULATOR_STATE";
const allowPersistRef = useRef(false);
const getMarginCalculatorPermissions = () => {
  try {
    const stored = sessionStorage.getItem("APP_USER_INFO");
    if (!stored) return { canAdd: false };

    const user = JSON.parse(stored);

    const service = user.services?.find(
      (s) => s.service === "Margin Calculator Management"
    );

    const ops = service?.operations || [];

    return {
      canAdd: ops.includes("CREATE"),
    };
  } catch {
    return { canAdd: false };
  }
};
const { canAdd } = getMarginCalculatorPermissions();

  // ----------------------------------------------------
  // FETCH QUOTES
  // ----------------------------------------------------
  const [stateRestored, setStateRestored] = useState(false);

useEffect(() => {
  const saved = sessionStorage.getItem(STATE_KEY);
  if (!saved) {
    setStateRestored(true);
    return;
  }

  const s = JSON.parse(saved);

  setQuoteInput(s.quoteInput ?? "");
  setQuoteFilter(s.quoteFilter ?? "");
  setSelectedCustomer(s.selectedCustomer ?? "");
  setCurrentPage(s.currentPage ?? 1);
  setSortField(s.sortField ?? "");
  setSortOrder(s.sortOrder ?? "");

  setStateRestored(true);
}, []);
useEffect(() => {
  if (!stateRestored) return;

  const stateToSave = {
    quoteInput,
    quoteFilter,
    selectedCustomer,
    currentPage,
    sortField,
    sortOrder,
  };

  sessionStorage.setItem(STATE_KEY, JSON.stringify(stateToSave));
}, [stateRestored, quoteInput, quoteFilter, selectedCustomer, currentPage, sortField, sortOrder]);

useEffect(() => {
   if (!stateRestored) return; 
  const fetchKey = JSON.stringify({
    page: currentPage,
    customer: selectedCustomer || "",
    quote: quoteFilter || "",
    sortField,
    sortOrder,
  });

  // 🔐 Prevent duplicate StrictMode execution
  if (lastFetchKeyRef.current === fetchKey) {
    return;
  }

  lastFetchKeyRef.current = fetchKey;

  const fetchQuotes = async () => {
    setIsPageLoading(true);
    try {
      const params = {
        quoteName: quoteFilter || undefined,
        customerId: selectedCustomer || undefined,
      };
      const sortParam = sortParamMap[sortField];
      if (sortParam && sortOrder) {
        params[sortParam] = sortOrder;
      }

      if (currentPage > 1) {
        params.page = currentPage;
      }

      const res = await axios.get("/margin-calculator", {
        params,
      });

      setTotalPages(Math.max(Number(res.data.totalPages) || 0, 1));

      setQuotes(
        res.data.margins.map(q => ({
          key: q.id,
          quoteName: q.name,
          customerName: q.customerName,
          customerPrice: formatMoney(q.customerPrice, q.currency),
          customerPriceValue: Number(q.customerPrice) || 0,
          totalPrice: formatMoney(q.totalPrice, q.currency),
          totalPriceValue: Number(q.totalPrice) || 0,
          grossMargin: formatMoney(q.grossMargin, q.currency),
          grossMarginValue: Number(q.grossMargin) || 0,
          marginPercent: `${q.grossMarginPercent.toFixed(2)}%`,
          marginPercentValue: Number(q.grossMarginPercent) || 0,
          createdAt: formatCreatedDate(q.createdAt),
          createdBy: getCreatedByName(q.createdBy),
        }))
      );
    } catch (err) {
      console.error("❌ Failed loading margins", err);
    } finally {
      setIsPageLoading(false);
      setHasLoadedQuotesOnce(true);
    }
  };

  fetchQuotes();
}, [stateRestored,currentPage, selectedCustomer, quoteFilter, sortField, sortOrder]);

const onQuoteInputChange = (e) => {
  setQuoteInput(e.target.value);
};

// const onQuoteSearch = () => {
//   setCurrentPage(1);
//   setQuoteFilter(quoteInput.trim());
// };
  // ----------------------------------------------------
  // LOAD CUSTOMERS BASED ON LOGIN ROLE
  // ----------------------------------------------------
 useEffect(() => {
  if (customersFetchedRef.current) return;
  customersFetchedRef.current = true;

  const loadCustomers = async () => {
  try {
    const res = await axios.get("/margin-calculator/dropdown/customers");

    setCustomerList(
      res.data.map(c => ({
        label: c.name,
        value: String(c.id),
        entity: c.entity || c.entityName || c.company || c.organization || c.businessName || "",
        customerCode: c.customerCode,
      }))
    );
  } catch (err) {
    console.error("❌ Failed loading customers", err);
    message.error("Failed loading customers");
  } finally {
    setIsCustomerLoading(false);
  }
};


  loadCustomers();
}, []);

useEffect(() => {
  const t = setTimeout(() => {
    setCurrentPage(1);
    setQuoteFilter(quoteInput.trim());
  }, 100);            // ⏳ calls API 400ms after last keystroke

  return () => clearTimeout(t);
}, [quoteInput]);
const OverflowCell = ({ text }) => {
  const { ref, showTooltip } = useOverflowTooltip(text);

  return (
    <CustomTooltip
      message={text}
      placement="top"
      color="#4A1476"
      show={showTooltip}
    >
      <span
        ref={ref}
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "inline-block",
          maxWidth: "100%",
        }}
      >
        {text || "-"}
      </span>
    </CustomTooltip>
  );
};

  // ----------------------------------------------------
  // TABLE COLUMNS
  // ----------------------------------------------------
  const columns = [
   {
     title: <div style={{ textAlign: "center" }}>Calculation Name</div>,
    dataIndex: "quoteName",
    key: "quoteName",
    width: "20%",
    render: (text, record) => (
      <span
        onClick={() => {
          allowPersistRef.current = true; // keep filters when coming back
          navigate(`/margin-calculator/${record.key}`);
        }}
        style={{
          color: "#662D91",
          cursor: "pointer",
        }}
      >
        <OverflowCell text={text} />
      </span>
    ),
  },
    {title: <div style={{ textAlign: "center" }}>Customer Name</div>,dataIndex: "customerName", key: "customerName",render: (text) => <OverflowCell text={text} />, },
    { title: <div style={{ textAlign: "center" }}>Customer Price</div>, dataIndex: "customerPrice",align: "right", key: "customerPrice" ,render: (val) => (
    <div style={{ textAlign: "right", width: "100%" }}>
      {val}
    </div>
  ),},
    {  title: <div style={{ textAlign: "center" }}>Total Price</div>, dataIndex: "totalPrice", align: "right",key: "totalPrice", sorter: (a, b) => a.totalPriceValue - b.totalPriceValue, sortOrder: sortField === "totalPrice" ? (sortOrder === "ASC" ? "ascend" : "descend") : null, render: (val) => (
    <div style={{ textAlign: "right", width: "100%" }}>
      {val}
    </div>
  ),
},
    { title: <div style={{ textAlign: "center" }}>Gross Margin</div>, dataIndex: "grossMargin", align: "right",key: "grossMargin", sorter: (a, b) => a.grossMarginValue - b.grossMarginValue, sortOrder: sortField === "grossMargin" ? (sortOrder === "ASC" ? "ascend" : "descend") : null,
  render: (val) => (
    <div style={{ textAlign: "right", width: "100%" }}>
      {val}
    </div>
  ),},
    { title: <div style={{ textAlign: "center" }}>Margin %</div>, dataIndex: "marginPercent",align: "right", key: "marginPercent", sorter: (a, b) => a.marginPercentValue - b.marginPercentValue, sortOrder: sortField === "marginPercent" ? (sortOrder === "ASC" ? "ascend" : "descend") : null,
  render: (val) => (
    <div style={{ textAlign: "right", width: "100%" }}>
      {val}
    </div>
  ), },
    {
      title: <div style={{ textAlign: "center" }}>Created Date</div>,
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) => <OverflowCell text={text} />,
    },
    {
      title: <div style={{ textAlign: "center" }}>Created By</div>,
      dataIndex: "createdBy",
      key: "createdBy",
      render: (text) => <OverflowCell text={text} />,
    },
    {
      title: <div style={{ textAlign: "center" }}>View</div>,
      key: "view",
      align: "center",
      width: 90,
      render: (_, record) => (
        <CustomTooltip message="View Calculation" placement="top" color="#4A1476">
          <Button
            className="mc-view-btn"
            icon={<EyeOutlined />}
            onClick={() => {
              allowPersistRef.current = true;
              navigate(`/margin-calculator/${record.key}`);
            }}
          />
        </CustomTooltip>
      ),
    },
  ];

  const handleTableChange = (_, __, sorter) => {
    const order = sorter?.order;
    setCurrentPage(1);
    if (!order) {
      setSortField("");
      setSortOrder("");
      return;
    }
    setSortField(sorter.field || sorter.columnKey);
    setSortOrder(order === "ascend" ? "ASC" : "DESC");
  };

  const renderPageNumbers = () => {
  const pages = [];

  pages.push(
    <button
      key={1}
      className={`pm-page-number ${currentPage === 1 ? "active" : ""}`}
      onClick={() => setCurrentPage(1)}
    >
      1
    </button>
  );

  if (currentPage > 3) {
    pages.push(<span key="el1" className="pm-page-ellipsis">...</span>);
  }

  for (
    let i = Math.max(2, currentPage - 1);
    i <= Math.min(currentPage + 1, totalPages - 1);
    i++
  ) {
    pages.push(
      <button
        key={i}
        className={`pm-page-number ${currentPage === i ? "active" : ""}`}
        onClick={() => setCurrentPage(i)}
      >
        {i}
      </button>
    );
  }

  if (currentPage < totalPages - 2) {
    pages.push(<span key="el2" className="pm-page-ellipsis">...</span>);
  }

  if (totalPages > 1) {
    pages.push(
      <button
        key={totalPages}
        className={`pm-page-number ${currentPage === totalPages ? "active" : ""}`}
        onClick={() => setCurrentPage(totalPages)}
      >
        {totalPages}
      </button>
    );
  }

  return pages;
};
useEffect(() => {
  return () => {
    // ❌ Clear state unless navigation is allowed
    if (!allowPersistRef.current) {
      sessionStorage.removeItem(STATE_KEY);
    }
  };
}, []);

const onCustomerChange = (v) => {
  setCurrentPage(1);          // ✅ RESET PAGE
  setSelectedCustomer(v);
};
const loadingIcon = <LoadingOutlined style={{ fontSize: 34, color: "#f50087" }} spin />;
if (!hasLoadedQuotesOnce && (isPageLoading || isCustomerLoading) && quotes.length === 0) {
  return (
    <div style={{ minHeight: "50vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
        <Spin indicator={loadingIcon} />
        <span style={{ color: "#f50087", fontWeight: 600 }}>Loading</span>
      </div>
    </div>
  );
}
  return (
    <div className="margin-calculator-container">
      {/* <Title level={3} style={{ marginBottom: 0 }}>
        Margin Calculator
      </Title>
      <Text type="secondary">Generate accurate quotes with margin validation</Text> */}
      <div className="breadcrumb-wrapper">
      <Breadcrumb>
      </Breadcrumb>
      </div>

      <Card className="quote-card" bordered={false}>
        {/* <Title level={5} style={{marginBottom:0, color:"#7509B8"}}>Margin Calculator</Title>
        <Text type="secondary" style={{color:"#7509B8"}}>
          Manage customer-specific pricing and discount structures
        </Text> */}

        <div className="filter-section">
          <div className="margin-filter-row">
            <div className="margin-filter-item margin-search-purple">
              <Input
                placeholder="Search calculation name"
                // style={{ width: 200, height: 38 }}
                value={quoteInput} // ✅ typing state
                onChange={onQuoteInputChange} // ✅ no API
                // onPressEnter={onQuoteSearch}
              />
            </div>

            <div className="margin-filter-item">
              <CustomerEntitySelect
              value={selectedCustomer}
              onChange={onCustomerChange}
              placeholder="Select Customer"
              width="100%"
              options={[
                {
                  value: "",
                  customerName: "Select Customer",
                  entityName: "-",
                  searchText: "select customer",
                },
                ...customerList.map((c) => ({
                  value: c.value,
                  customerName: c.label || "-",
                  entityName: c.entityName || c.entity || c.company || c.organization || c.businessName || "-",
                  customerCode: c.customerCode || "-",
                  searchText: `${c.label || ""} ${
                    c.entityName || c.entity || c.company || c.organization || c.businessName || ""
                  } ${c.customerCode||""}`,
                })),
              ]}
            />
            </div>
          </div>

          {/* 🚀 Navigate to NEW PAGE instead of View 2 */}
          {canAdd && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={{ background: "#2D0844", borderColor: "#2D0844", height:"38px" }}
             onClick={() => {
    allowPersistRef.current = true;   // ✅ allow state persistence
    navigate("/calculate-margin");
  }}
          >
            Calculate Margin
          </Button>)}
        </div>

        <Table
          columns={columns}
          dataSource={quotes}
          pagination={false}
          className="margin-table"
          onChange={handleTableChange}
          showSorterTooltip={false}
        />
        <div className="pm-pagination-frame">
  <div className="pm-pagination-info">
    Page {currentPage} of {totalPages}
  </div>

  <div className="pm-pagination-controls">
    <button
      className="pm-pagination-arrow"
      disabled={currentPage === 1}
      onClick={() => setCurrentPage(currentPage - 1)}
    >
      ‹
    </button>

    <div className="pm-pagination-numbers">
      {renderPageNumbers()}
    </div>

    <button
      className="pm-pagination-arrow"
      disabled={currentPage === totalPages}
      onClick={() => setCurrentPage(currentPage + 1)}
    >
      ›
    </button>
  </div>
</div>

      </Card>
    </div>
  );
};

export default MarginCalculator;
